import React from 'react';
import { Menu } from 'antd';
import { TrafficVolumeMeteoForecast } from 'app/model/SOL49';
import { Flex } from 'app/components/common/Flex';
import { AirspaceRef, FlightLevelBand, TimePeriod } from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import { TVLoadProps } from 'app/components/dashboard/network/flows/TVLoad';
import memoizeOne from 'memoize-one';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { ShowTvDetailEvent, UpdateTvSelection } from 'app/common/eventbus/Events';
import AppContext from 'app/common/context/AppContext';
import { MenuUtils } from 'app/components/dashboard/common/MenuUtils';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { MeasureLoadBar } from 'app/components/dashboard/network/flows/svg/MeasureLoadBar';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { RootState } from '../../../../../redux/store';
import {
    addToFlowSection,
    createFlowSection,
    selectUserCustomFlows,
} from '../../../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps } from 'react-redux';

const mapStateToProps = (state: RootState) => {
    return {
        customFlowSections: selectUserCustomFlows(state),
    };
};
const mapMapDispatchToProps = {
    createFlowSection,
    addToFlowSection,
};

const connector = connect(mapStateToProps, mapMapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface MapTvConvectionContextMenuProps extends ObjectTypeContextMenu, PropsFromRedux {
    convections: Array<TrafficVolumeMeteoForecast>;
    tvLoads: Array<TVLoadProps>;

    onClose(): void;
}

const ACTION_TV_IMPACTED_FLIGHTS = 'tv-impacted-flights';
const ACTIONS = [ACTION_TV_IMPACTED_FLIGHTS];

export class MapTvConvectionContextMenu extends React.PureComponent<MapTvConvectionContextMenuProps> {
    render() {
        if (this.props.group) {
            return (
                <Menu
                    items={[
                        {
                            key: 'polygon-convection-detail',
                            label: (
                                <span className="detailed-subpanel-title">
                                    Convection ({this.props.convections?.length})
                                </span>
                            ),
                            children: this.props.convections?.flatMap(c => {
                                const items: { [tv: string]: AirspaceRef } = {};
                                items[c.id] = c.airspaceRef;
                                return [
                                    {
                                        key: `polygon-convection-detail-${c.id}`,
                                        label: this.renderGenericInfo(c),
                                        className: 'menuItemConvection',
                                    },
                                    ...this.getMenuItems(
                                        c.validityPeriod,
                                        items,
                                        this.getTvLoad(c.id, this.props.tvLoads),
                                    ),
                                ];
                            }),
                        },
                    ]}
                    triggerSubMenuAction="click"
                    onClick={this.handleOnClick}
                />
            );
        } else {
            return this.props.convections.map(c => this.renderConvection(c))?.flat();
        }
    }

    getTvLoad = memoizeOne((tvName: string, tvLoads: Array<TVLoadProps>): TVLoadProps => {
        return tvLoads.find(tvl => tvl.tvName === tvName);
    });

    private renderGenericInfo(convection: TrafficVolumeMeteoForecast) {
        return (
            <Flex direction="vertical" key={`${convection.id}`}>
                <span>Risk Level</span>
                <p
                    style={{ textAlign: 'center', lineHeight: '1.5em' }}
                    className={this.getRiskLevelClassName(convection.level)}>
                    {this.getRiskLevelLabel(convection.level)}
                </p>
                <p style={{ color: 'white', lineHeight: '1em' }}>
                    <span style={{ float: 'left' }}>{this.getFlightLevelString(convection.flightLevelBand)}</span>
                    <span style={{ float: 'right' }}>{this.getTimePeriodString(convection.validityPeriod)}</span>
                </p>
            </Flex>
        );
    }

    renderConvection(convection: TrafficVolumeMeteoForecast) {
        return (
            <Flex direction="vertical" style={{ margin: '1em' }} key={`${convection.id}`}>
                {this.renderGenericInfo(convection)}
                {this.renderTvConvectionDetail(convection)}
            </Flex>
        );
    }

    renderTvConvectionDetail = (tvConvection: TrafficVolumeMeteoForecast) => {
        const items: { [tv: string]: AirspaceRef } = {};
        items[tvConvection.id] = tvConvection.airspaceRef;
        return (
            <Flex direction="vertical">
                <Menu
                    key="polygon-convection-detail-menu"
                    items={this.getMenuItems(
                        tvConvection.validityPeriod,
                        items,
                        this.getTvLoad(tvConvection.id, this.props.tvLoads),
                    )}
                    triggerSubMenuAction="click"
                    onClick={this.handleOnClick}
                />
            </Flex>
        );
    };

    getMenuItems = (timePeriod: TimePeriod, tvAirspace: { [tv: string]: AirspaceRef }, load: TVLoadProps) => {
        const mm = load.monitoredMeasures;
        const openMeasureItems = mm
            .filter(mm => mm.measureLoad.some(load => TimePeriodUtils.intersects(load.timePeriod, timePeriod)))
            .map((mm, idx) => ({
                label: `Open/edit ${mm.measure.measureId.id}`,
                key: `open-edit-${mm.measure.measureId.id}-${idx}`,
                onClick: () => ModalHandler.showRegulationEditor(mm.measure.measureId.id, 'reg'),
            }));
        return Object.keys(tvAirspace).map(tv => {
            let subItems = undefined;
            if (!AuthorisationUtils.isFmpMapUser()) {
                subItems = [
                    {
                        label: 'Show details (graph)',
                        key: `show-tv-details-${tv}`,
                        onClick: () => ShowTvDetailEvent.fireEvent(tv),
                    },
                    ...openMeasureItems,
                    {
                        label: 'Add to selection',
                        key: `add-to-selection-${tv}`,
                        onClick: () => AppContext.eventBus.publish(new UpdateTvSelection('add', [tv])),
                    },
                    {
                        label: 'Impacted Flights',
                        key: this.actionKey(ACTION_TV_IMPACTED_FLIGHTS, tv),
                        className: 'menuItemConvection',
                    },
                    { key: 'divider-1', type: 'divider' },
                    MenuUtils.addToCustomSectionMenu(
                        this.props.customFlowSections,
                        payload => this.props.addToFlowSection(payload),
                        payload => this.props.createFlowSection(payload),
                        [tv],
                        '',
                        this.props.onClose,
                    ),
                ];
            }
            return {
                key: tv,
                label: this.getLabel(this.getTvLoad(tv, this.props.tvLoads), timePeriod),
                children: subItems,
            };
        });
    };

    private getLabel = (tvLoad: TVLoadProps, timePeriod: TimePeriod) => {
        return (
            <Flex grow={false} onClick={event => this.handleTvLoadClick(event, tvLoad.tvName)}>
                <Flex size="6.5em">
                    TV: <span style={{ color: 'white' }}>{tvLoad.tvName}</span>
                </Flex>
                <Flex direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                    {tvLoad?.entryLoad && (
                        <EntryLoadBar
                            key="entry-load"
                            loadValues={tvLoad?.entryLoad}
                            monitoredPeriod={timePeriod}
                            activePeriods={tvLoad.activePeriods}
                            currentTime={AppContext.getCurrentTime()}
                            drawBottomLine={true}
                            barHeightEm={0.5}
                        />
                    )}
                    {tvLoad?.occupancyLoad &&
                        tvLoad?.occupancyLoad.map(occupancyValues => {
                            return (
                                <OccupancyLoadBar
                                    key={'occupancy-load-' + occupancyValues.durationInMin}
                                    loadValues={occupancyValues.occupancyLoadValues}
                                    monitoredPeriod={timePeriod}
                                    activePeriods={tvLoad.activePeriods}
                                    currentTime={AppContext.getCurrentTime()}
                                    drawBottomLine={true}
                                    barHeightEm={0.5}
                                />
                            );
                        })}
                    {tvLoad?.monitoredMeasures?.map(measure => (
                        <MeasureLoadBar
                            key={'measure-load-' + measure.measure.measureId.id}
                            loadValues={measure.measureLoad}
                            monitoredPeriod={timePeriod}
                            currentTime={AppContext.getCurrentTime()}
                            barHeightEm={0.5}
                        />
                    ))}
                </Flex>
            </Flex>
        );
    };

    private getRiskLevelLabel(riskLevel: number): string {
        switch (riskLevel) {
            case 0:
                return 'NONE';
            case 1:
                return 'LOW';
            case 2:
                return 'MEDIUM';
            case 3:
                return 'HIGH';
            case 4:
                return 'CRITICAL';
        }
        return null;
    }

    private getRiskLevelClassName(riskLevel: number): string {
        switch (riskLevel) {
            case 0:
                return 'None';
            case 1:
                return 'yellow';
            case 2:
                return 'orange';
            case 3:
                return 'red';
            case 4:
                return 'purple';
        }
        return null;
    }

    private getFlightLevelString(flBand: FlightLevelBand): string {
        if (flBand) {
            return `${FlightLevelUtils.toString(flBand.min)} > ${FlightLevelUtils.toString(flBand.max)}`;
        }
    }

    private getTimePeriodString(timePeriod: TimePeriod): string {
        if (timePeriod) {
            return `${TimeUtils.formatHHMM(timePeriod.from)}-${TimeUtils.formatHHMM(timePeriod.to)}`;
        }
    }

    actionKey(action: string, tvName: string) {
        return `${action}_${tvName}`;
    }

    handleOnClick = event => {
        const parsedKey = event.key.split('_');
        const action = parsedKey[0].trim();
        //check if is a valid action
        if (!ACTIONS.includes(action)) return;

        const tvName = parsedKey[1];

        const tvLoad = this.props.tvLoads.find(tvLoad => tvLoad.tvName === tvName);
        const convection = this.props.convections.find(convetion => convetion.id === tvName);

        //check if is a valid tv
        if (!tvLoad || !convection) return;

        this.props.onClose();

        switch (action) {
            case ACTION_TV_IMPACTED_FLIGHTS:
                ModalHandler.showConvectionImpactedFlights(convection);
                break;
        }
    };

    handleTvLoadClick(event: React.MouseEvent<HTMLDivElement>, tvName: string) {
        event.stopPropagation();
        this.props.onClose();
        ShowTvDetailEvent.fireEvent(tvName);
    }
}

export default connector(MapTvConvectionContextMenu);
